import * as React from 'react'
import { ChampionIcon } from './champion-icon'

import { SLOT_ROW_GAP_ALIGNMENT_OFFSET } from './constants'
import { TeamSlot } from './team-slot'
import type {
  BracketDirection,
  BracketDisplayMode,
  BracketMakerCopy,
  BracketStructure,
  BracketTheme,
  Match,
  PositionedMatch,
  WinnerSide,
} from './types'

type MatchNodeProps = {
  copy: BracketMakerCopy
  direction: BracketDirection
  layout: PositionedMatch
  match: Match
  matchIndex: number
  displayMode: BracketDisplayMode
  normalizedTeamCount: number
  seedOrder: number[] | null
  onClearSeedImage: (seedIndex: number) => void
  onPickWinner: (
    roundIndex: number,
    matchIndex: number,
    side: WinnerSide,
    name: string,
  ) => void
  onRenameSeed: (seedIndex: number, value: string) => void
  onRequestSeedImage: (seedIndex: number) => void
  roundCount: number
  roundIndex: number
  showNames: boolean
  style?: React.CSSProperties
  structure: BracketStructure
  theme: BracketTheme
  visible: boolean
}

function MatchNodeView({
  copy,
  direction,
  displayMode,
  layout,
  match,
  matchIndex,
  normalizedTeamCount,
  seedOrder,
  onClearSeedImage,
  onPickWinner,
  onRenameSeed,
  onRequestSeedImage,
  roundCount,
  roundIndex,
  showNames,
  style,
  structure,
  theme,
  visible,
}: MatchNodeProps) {
  const isFinalRound = roundIndex === roundCount - 1
  const useFinalCard = isFinalRound && structure !== 'single'
  const useHorizontalFinal = useFinalCard && direction === 'horizontal'
  const showVerticalFinalAccent = useFinalCard && direction === 'vertical'
  const isFirstRound = roundIndex === 0
  const showImages = displayMode === 'image'
  const hasExternalNameRow = showImages && showNames

  const winnerChosen = (side: WinnerSide) =>
    match.selection === side ||
    (!match.selection &&
      match.auto &&
      !!match.winner &&
      !!match[side] &&
      match.winner === match[side])

  const slotMeta: Record<
    WinnerSide,
    {
      image?: string | null
      isWinner: boolean
      name: string
      seedIndex?: number
    }
  > = {
    home: {
      name: match.home,
      image: match.homeImage,
      isWinner: winnerChosen('home'),
      seedIndex: isFirstRound ? matchIndex * 2 : undefined,
    },
    away: {
      name: match.away,
      image: match.awayImage,
      isWinner: winnerChosen('away'),
      seedIndex: isFirstRound ? matchIndex * 2 + 1 : undefined,
    },
  }

  const renderSlot = (side: WinnerSide) => {
    const { name, image, isWinner, seedIndex } = slotMeta[side]
    const canEditSeed =
      isFirstRound &&
      seedIndex !== undefined &&
      (seedOrder?.[seedIndex] ?? seedIndex) < normalizedTeamCount

    return (
      <TeamSlot
        name={name}
        image={image}
        placeholder={copy.bye}
        isWinner={isWinner}
        autoLabel={copy.autoWin}
        autoWin={isWinner && match.auto}
        onPick={() => onPickWinner(roundIndex, matchIndex, side, name)}
        editable={canEditSeed}
        onRename={
          canEditSeed && seedIndex !== undefined
            ? (value) => onRenameSeed(seedIndex, value)
            : undefined
        }
        onRequestImage={
          canEditSeed && seedIndex !== undefined
            ? () => onRequestSeedImage(seedIndex)
            : undefined
        }
        onClearImage={
          canEditSeed && seedIndex !== undefined
            ? () => onClearSeedImage(seedIndex)
            : undefined
        }
        showName={showNames}
        showImage={showImages}
        theme={theme}
      />
    )
  }

  const nodeStyle =
    direction === 'horizontal'
      ? {
          gridColumnStart: layout.columnIndex,
          gridRowStart: layout.startRow,
          gridRowEnd: layout.startRow + layout.span,
          transform: layout.translateY
            ? `translateY(${layout.translateY}px)`
            : undefined,
        }
      : style
  const nodeClassName =
    direction === 'horizontal'
      ? useHorizontalFinal
        ? theme.gridMatchFinal
        : theme.gridMatch
      : 'absolute flex overflow-visible'

  return (
    <div
      style={nodeStyle}
      className={nodeClassName}
      data-bracket-match={`${roundIndex}-${matchIndex}`}
      data-rendered={visible}
      aria-hidden={visible ? undefined : true}
    >
      {visible && (
        <>
          {useHorizontalFinal ? (
            <div className="relative h-full w-full">
              <div className="absolute left-0 top-1/2 flex w-36 -translate-y-1/2 items-center justify-center">
                {renderSlot('home')}
              </div>

              <div className="absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-2">
                <ChampionIcon className="pointer-events-none drop-shadow-sm" />
                <span className={theme.matchVs}>VS</span>
              </div>

              <div className="absolute right-0 top-1/2 flex w-36 -translate-y-1/2 items-center justify-center">
                {renderSlot('away')}
              </div>
            </div>
          ) : direction === 'vertical' && showVerticalFinalAccent ? (
            <div className="relative h-full w-full">
              <div className="absolute left-1/2 top-0 flex w-40 -translate-x-1/2 items-start justify-center">
                {renderSlot('home')}
              </div>

              <div className="absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-2">
                <ChampionIcon className="pointer-events-none drop-shadow-sm" />
                <span className={theme.matchVs}>VS</span>
              </div>

              <div className="absolute bottom-0 left-1/2 flex w-40 -translate-x-1/2 items-end justify-center">
                {renderSlot('away')}
              </div>
            </div>
          ) : direction === 'vertical' ? (
            <div className="relative h-full w-full">
              <div
                className="absolute top-0 flex h-full w-36 -translate-x-1/2 items-center justify-center"
                style={{
                  left: `calc(25% - ${SLOT_ROW_GAP_ALIGNMENT_OFFSET}px)`,
                }}
              >
                {renderSlot('home')}
              </div>

              <div
                className="absolute left-[50%] top-0 z-10 flex h-full w-12 -translate-x-1/2 items-center justify-center"
                style={{
                  marginLeft: hasExternalNameRow ? 0 : showImages ? -14 : 0,
                }}
              >
                <span className={theme.matchVs}>VS</span>
              </div>

              <div
                className="absolute top-0 flex h-full w-36 -translate-x-1/2 items-center justify-center"
                style={{
                  left: `calc(75% + ${SLOT_ROW_GAP_ALIGNMENT_OFFSET}px)`,
                }}
              >
                {renderSlot('away')}
              </div>
            </div>
          ) : (
            <div className="relative h-full w-full">
              <div
                className="absolute left-0 flex w-full -translate-y-1/2 items-center justify-center"
                style={{
                  top: `calc(25% - ${SLOT_ROW_GAP_ALIGNMENT_OFFSET}px)`,
                }}
              >
                {renderSlot('home')}
              </div>

              <div
                className="absolute left-0 top-[50%] z-10 flex w-full -translate-y-1/2 items-center justify-center"
                style={{
                  marginTop: hasExternalNameRow ? 0 : showImages ? -14 : 0,
                }}
              >
                <span className={theme.matchVs}>VS</span>
              </div>

              <div
                className="absolute left-0 flex w-full -translate-y-1/2 items-center justify-center"
                style={{
                  top: `calc(75% + ${SLOT_ROW_GAP_ALIGNMENT_OFFSET}px)`,
                }}
              >
                {renderSlot('away')}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export const MatchNode = React.memo(MatchNodeView, (previous, next) => {
  const keys = Object.keys(previous) as (keyof MatchNodeProps)[]
  return keys.every((key) => {
    if (key === 'match') {
      return (Object.keys(previous.match) as (keyof Match)[]).every(
        (field) => previous.match[field] === next.match[field],
      )
    }
    if (key === 'style') {
      const before = previous.style || {}
      const after = next.style || {}
      return (
        Object.keys(before).length === Object.keys(after).length &&
        (Object.keys(before) as (keyof React.CSSProperties)[]).every(
          (field) => before[field] === after[field],
        )
      )
    }
    return previous[key] === next[key]
  })
})
